import {
    UserVaultPositionExpanded,
    UserVaultPnl,
    StakeStatus,
    useUserVaultPnlFromDepositTerms,
    DepositTermSummary,
    isRecentVaultDepositOrWithdrawal
} from "@bridgesplit/abf-react";
import { MediaQuery, TooltipText, StatProps } from "@bridgesplit/ui";
import { useMediaQuery } from "@mui/material";
import { PositionCardStats, VaultProfitLoss } from "app/components/common";
import { COPY } from "app/constants";

import { VaultAmountDisplay } from "./VaultAmountDisplay";
import { VaultPositionApy } from "./VaultPositionCard";
import { VaultPendingPnl } from "./VaultPendingPnl";

export function VaultPositionStats({
    vaultPosition,
    query,
    status,
    depositTerms
}: {
    vaultPosition: UserVaultPositionExpanded | undefined;
    query: MediaQuery;
    status?: StakeStatus;
    depositTerms: DepositTermSummary | undefined;
}) {
    const isMobile = useMediaQuery(query.below);

    const userVaultPnl = useUserVaultPnlFromDepositTerms(depositTerms, vaultPosition?.vaultExpanded, vaultPosition);

    const stats = getVaultPositionStats(vaultPosition, isMobile, userVaultPnl, status);

    return <PositionCardStats loading={!vaultPosition || !userVaultPnl} stats={stats} forceColumn={!isMobile} />;
}
function VaultPositionValue({ vaultPosition }: { vaultPosition: UserVaultPositionExpanded | undefined }) {
    return <VaultAmountDisplay vaultPosition={vaultPosition} variant="total" />;
}

function getVaultPositionStats(
    vaultPosition: UserVaultPositionExpanded | undefined,
    isMobile: boolean,
    userVaultPnl: UserVaultPnl | undefined,
    status?: StakeStatus
) {
    const showPnl = status === StakeStatus.Active && (userVaultPnl?.lifetimePnl || userVaultPnl?.currentPnl);
    const isPnlPending = isRecentVaultDepositOrWithdrawal(userVaultPnl?.mostRecentDepositOrWithdrawalTimestamp);

    const stats: StatProps[] = [
        {
            value: <VaultPositionApy vaultPosition={vaultPosition} />,
            caption: "APY",
            hide: !isMobile
        },
        {
            value: <VaultPositionValue vaultPosition={vaultPosition} />,
            caption: COPY.USER_POSITION_TERM
        },
        {
            value: (
                <VaultProfitLoss
                    variant="body1"
                    vaultPosition={vaultPosition}
                    userVaultPnl={userVaultPnl}
                    isPending={isPnlPending}
                />
            ),
            caption: (
                <TooltipText color="caption" helpText={getVaultPnlTooltipText(isPnlPending, userVaultPnl)}>
                    {COPY.INTEREST_EARNED_LABEL}
                </TooltipText>
            ),
            hide: !showPnl || isPnlPending
        },
        {
            value: <VaultPendingPnl />,
            caption: COPY.INTEREST_EARNED_LABEL,
            hide: !isPnlPending
        }
    ].filter((stat) => !stat.hide);

    return stats;
}

function getVaultPnlTooltipText(isPnlPending: boolean, userVaultPnl: UserVaultPnl | undefined): string | undefined {
    if (isPnlPending) return undefined;
    if (userVaultPnl?.lifetimePnl) return COPY.LIFETIME_PNL_TOOLTIP;
    return COPY.CURRENT_PNL_TOOLTIP;
}
